import { useEffect, useState, MouseEvent, Fragment } from 'react';
import Map, { Marker, NavigationControl, Popup } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import './mapbox-map.scss';
import { PropertySearchResTypeData } from '../../services/api';
import { PropertyDetailsResponseData } from '../../models/details.models';
import { useGetCurrentUser } from '../../hooks/auth.ts';
import { useGetFavorites } from '../../api/favorites';
import { ResultsListItem } from '../Results/ResultsListItem';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import VrpanoRoundedIcon from '@mui/icons-material/VrpanoRounded';
import { primary, secondary } from '../../constants/theme/colors.tsx';
import { ResultsAndMapLoading } from '../Results/ResultsAndMapLoading';

interface MapboxMapProps {
  results?: PropertySearchResTypeData[];
  addressDetails?: PropertyDetailsResponseData;
  isLoading?: boolean;
}

export const MapboxMapWithMarkers = ({
  results,
  isLoading = false,
}: MapboxMapProps) => {
  const isLargeScreen = useMediaQuery('(min-width: 901px)');
  const [chosenLocation, setChosenLocation] =
    useState<PropertySearchResTypeData | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [viewState, setViewState] = useState<{
    longitude: number;
    latitude: number;
    zoom: number;
  } | null>(null);
  const [hoveredMarker, setHoveredMarker] = useState<string | null>();
  const [mapViewType, setMapViewType] = useState<string | null>('map');

  const user = useGetCurrentUser();
  const { data: favoritesData } = useGetFavorites(user.data?.userId ?? '');

  useEffect(() => {
    if (results && results.length > 0) {
      setChosenLocation(null);
      setShowPopup(false);
      setViewState({
        longitude: results[0].longitude,
        latitude: results[0].latitude,
        zoom: 10,
      });
    }
  }, [results]);

  const handleMapViewChange = (
    _event: MouseEvent<HTMLElement>,
    newMapView: string | null
  ) => {
    if (newMapView) {
      setMapViewType(newMapView);
    }
  };

  const formatNumber = (price: number) => {
    if (price >= 1000000) {
      const millions = price / 1000000;
      return `${millions % 1 === 0 ? millions : millions.toFixed(1)}M`;
    } else if (price >= 1000) {
      const thousands = price / 1000;
      return `${thousands % 1 === 0 ? thousands : thousands.toFixed(1)}K`;
    }
    return price.toString();
  };

  if (!viewState || isLoading) {
    return <ResultsAndMapLoading />;
  }

  return (
    <div style={{ height: '100%' }}>
      <div className="mapbox-map" data-testid="mapbox-map-wrapper">
        <div className="map-container">
          {isLoading ? (
            <ResultsAndMapLoading />
          ) : (
            <Map
              data-mcm-mapbox-map
              {...viewState}
              mapboxAccessToken={
                import.meta.env.VITE_MAPBOX_PUBLIC_KEY as string
              }
              onMove={(evt) => {
                setChosenLocation(null);
                setViewState(evt.viewState);
              }}
              style={{
                width: '100%',
                height: '100%',
              }}
              mapStyle={
                mapViewType === 'map'
                  ? 'mapbox://styles/mapbox/streets-v9'
                  : 'mapbox://styles/mapbox/satellite-v9'
              }
            >
              <ToggleButtonGroup
                data-map-view-toggle
                value={mapViewType}
                exclusive
                onChange={handleMapViewChange}
                aria-label="text alignment"
              >
                <ToggleButton
                  value="satellite"
                  aria-label="left aligned"
                  size="small"
                  data-sat-view-button
                >
                  <PublicRoundedIcon />
                  <span>SAT</span>
                </ToggleButton>
                <ToggleButton
                  value="map"
                  aria-label="centered"
                  size="small"
                  data-map-view-button
                >
                  <VrpanoRoundedIcon />
                  <span>MAP</span>
                </ToggleButton>
              </ToggleButtonGroup>
              <NavigationControl
                showZoom={isLargeScreen}
                showCompass
                position="bottom-right"
              />
              {results &&
                results?.map((loc) => {
                  if (!loc.latitude || !loc.longitude) {
                    return null;
                  }
                  return (
                    <Fragment key={loc.id}>
                      <div
                        onMouseEnter={() => setHoveredMarker(loc.id)}
                        onMouseLeave={() => setHoveredMarker(null)}
                      >
                        <Marker
                          key={loc.id}
                          longitude={loc.longitude}
                          latitude={loc.latitude}
                          anchor="bottom"
                          onClick={() => {
                            setChosenLocation(loc);
                            setShowPopup(true);
                          }}
                        >
                          <Box
                            sx={{
                              color: 'white',
                              position: 'relative',

                              border: '1px solid white',
                              backgroundColor:
                                hoveredMarker === loc.id
                                  ? secondary.main
                                  : primary.main,
                            }}
                          >
                            <Typography sx={{ paddingX: '5px' }}>
                              {formatNumber(loc.mlsListingPrice)}
                            </Typography>
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: '-8px',
                                left: '50%',
                                maxWidth: 0,
                                height: 0,
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderTop: ` 10px solid ${
                                  hoveredMarker === loc.id
                                    ? secondary.main
                                    : primary.main
                                }`,
                                transform: 'translateX(-50%)',
                              }}
                            />
                          </Box>
                        </Marker>
                      </div>
                    </Fragment>
                  );
                })}
              {showPopup && chosenLocation && (
                <Popup
                  longitude={chosenLocation.longitude}
                  latitude={chosenLocation.latitude}
                  anchor="bottom"
                  closeOnClick={false}
                  onClose={() => setShowPopup(false)}
                  className="mcm-map-popup"
                >
                  <ResultsListItem
                    favorites={favoritesData || []}
                    result={chosenLocation}
                  />
                </Popup>
              )}
            </Map>
          )}
        </div>
      </div>
      {chosenLocation && (
        <div
          onClick={() => setChosenLocation(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};
